import { createFileRoute } from '@tanstack/react-router';
import { assertPublicToolsEnabled } from '@/lib/public-tools-feature';
import { FAQButton } from '@/components/astro/new-design-25/ui/FAQ-Button';
import { Footer } from '@/components/astro/new-design-25/ui/Footer';
import { NavBar } from '@/components/astro/new-design-25/ui/NavBar';
import { TryListeningBanner } from '@/components/astro/new-design-25/ui/TryListeningBanner';
import { GridBackground } from '@/components/shared-ui/grid-background';
import { localizeHref } from '@/paraglide/runtime';
import { hreflangLinks, seo } from '@/seo/seo';
import mobileToolPng from '@listening/assets/astro/images/tools/mobile-tool.png';
import pdfLanguagesPng from '@listening/assets/astro/images/tools/pdf-languages.png';
import pdfToolsPng from '@listening/assets/astro/images/tools/pdf-tools.png';
import researchToolPng from '@listening/assets/astro/images/tools/research-tool.png';
import textToAudioPng from '@listening/assets/astro/images/tools/text-to-audio.png';
import textToAudioLanguagesPng from '@listening/assets/astro/images/tools/text-to-audio-languages.png';
import webToAudioPng from '@listening/assets/astro/images/tools/web-to-audio.png';

type CategoryId = string;

type ToolLink = {
  name: string;
  href: string;
};

type Category = {
  id: CategoryId;
  title: string;
  eyebrow: string;
  previewBg: string;
  tools: ToolLink[];
};

export const toolCategories: Category[] = [
  {
    id: 'read-aloud-tools',
    title: 'Read-aloud & TTS tools',
    eyebrow: 'Read text out loud',
    previewBg: '#f7ecdb',
    tools: [
      { name: 'Read aloud reader', href: '/read-aloud-reader' },
      { name: 'Read out loud', href: '/read-out-loud' },
      { name: 'Read aloud text', href: '/read-aloud-text' },
      { name: 'Text to speech read aloud', href: '/text-to-speech-read-aloud' },
      { name: 'Read text out loud', href: '/read-text-out-loud' },
      { name: 'Text to speech', href: '/text-to-speech' },
      { name: 'Text to audio', href: '/text-to-audio' },
      { name: 'Text to MP3', href: '/text-to-mp3' },
      { name: 'Text to speech software', href: '/text-to-speech-software' },
      { name: 'TTS software', href: '/tts-software' },
      {
        name: 'Google text to speech software',
        href: '/google-text-to-speech-software',
      },
      { name: 'TTS for computer', href: '/tts-for-computer' },
      {
        name: 'AI text to speech software',
        href: '/ai-text-to-speech-software',
      },
      { name: 'Speech synthesis software', href: '/speech-synthesis-software' },
    ],
  },
  {
    id: 'pdf-documents',
    title: 'PDF & documents',
    eyebrow: 'Files, books, documents',
    previewBg: '#e8eaf3',
    tools: [
      { name: 'PDF to audio', href: '/pdf-to-audio' },
      { name: 'PDF to MP3', href: '/pdf-to-mp3' },
      { name: 'PDF to audiobook', href: '/pdf-to-audiobook' },
      { name: 'Read PDF', href: '/read-pdf' },
      { name: 'PDF read aloud', href: '/pdf-read-aloud' },
      { name: 'PDF audio reader', href: '/pdf-audio-reader' },
      { name: 'Word read aloud', href: '/word-read-aloud' },
      { name: 'Document to audio', href: '/document-to-audio' },
      { name: 'eBook reader TTS', href: '/ebook-reader-tts' },
    ],
  },
  {
    id: 'voice-generators',
    title: 'Voice & audio generators',
    eyebrow: 'Generate voice audio',
    previewBg: '#f4ecd3',
    tools: [
      { name: 'Text to voice generator', href: '/text-to-voice-generator' },
      {
        name: 'Text to speech generator',
        href: '/text-to-speech-generator',
      },
      { name: 'AI voice text to speech', href: '/ai-voice-text-to-speech' },
      { name: 'Text into audio', href: '/text-into-audio' },
      { name: 'Text to audio online', href: '/text-to-audio-online' },
      { name: 'Text to audio converter', href: '/text-to-audio-converter' },
      {
        name: 'Natural-sounding text to speech',
        href: '/natural-sounding-text-to-speech',
      },
      { name: 'AI voice reader', href: '/ai-voice-reader' },
      { name: 'Female voice generator', href: '/female-voice-generator' },
      { name: 'Male voice generator', href: '/male-voice-generator' },
    ],
  },
  {
    id: 'articles-web',
    title: 'Articles & web',
    eyebrow: 'Paste a link',
    previewBg: '#e6ede1',
    tools: [
      { name: 'Listen to articles online', href: '/listen-to-articles-online' },
      {
        name: 'Text to speech for websites',
        href: '/text-to-speech-for-websites',
      },
      { name: 'Web page reader', href: '/web-page-reader' },
      { name: 'Read web page out loud', href: '/read-web-page-out-loud' },
      { name: 'Websites that read to you', href: '/websites-that-read-to-you' },
      { name: 'Online text reader', href: '/online-text-reader' },
      { name: 'Online text to speech', href: '/online-text-to-speech' },
    ],
  },
  {
    id: 'apps-extensions-desktop',
    title: 'Apps, extensions & desktop',
    eyebrow: 'Mobile, Chrome, OS',
    previewBg: '#dde9eb',
    tools: [
      { name: 'Text to speech app', href: '/text-to-speech-app' },
      { name: 'TTS app', href: '/tts-app' },
      { name: 'PDF reader for Android', href: '/pdf-reader-for-android' },
      { name: 'TTS app for Android', href: '/tts-app-for-android' },
      { name: 'Android text to speech', href: '/android-text-to-speech' },
      { name: 'iPhone read PDF aloud', href: '/iphone-read-pdf-aloud' },
      { name: 'Browser extension', href: '/browser-extension' },
      {
        name: 'Text to speech Chrome extension',
        href: '/text-to-speech-chrome-extension',
      },
      { name: 'Chrome TTS plugin', href: '/chrome-tts-plugin' },
      { name: 'Chromebook extension', href: '/chromebook-extension' },
      { name: 'TTS extension for Chrome', href: '/tts-extension-for-chrome' },
      {
        name: 'Read aloud Chrome extension',
        href: '/read-aloud-chrome-extension',
      },
      { name: 'Edge browser reader', href: '/edge-browser-reader' },
      { name: 'Text to speech for Mac', href: '/text-to-speech-for-mac' },
      { name: 'Mac OS TTS', href: '/mac-os-tts' },
      { name: 'PDF reader for Windows', href: '/pdf-reader-for-windows' },
      { name: 'Text to speech Windows', href: '/text-to-speech-windows' },
      { name: 'PDF reader for Linux', href: '/pdf-reader-for-linux' },
      { name: 'OCR reader online', href: '/ocr-reader-online' },
    ],
  },
  {
    id: 'research-study',
    title: 'Research & study',
    eyebrow: 'Papers, textbooks, focus',
    previewBg: '#f4ecd3',
    tools: [
      { name: 'Academic paper reader', href: '/academic-paper-reader' },
      { name: 'Research paper audio', href: '/research-paper-audio' },
      { name: 'TTS for students', href: '/tts-for-students' },
      { name: 'Audiobook creator', href: '/audiobook-creator' },
      { name: 'Listen to textbooks', href: '/listen-to-textbooks' },
      { name: 'Research paper listener', href: '/research-paper-listener' },
      { name: 'Academic document audio', href: '/academic-podcast-creator' },
      { name: 'PhD thesis assistant', href: '/phd-thesis-research-assistant' },
      {
        name: 'Grant proposal review',
        href: '/grant-proposal-audio-review-tool',
      },
      { name: 'Audio note taking', href: '/audio-note-taking' },
      { name: 'Speed reading', href: '/speed-reading' },
      { name: 'Study tool', href: '/study-tool' },
      {
        name: 'Dyslexia reading assistant',
        href: '/dyslexia-reading-assistant',
      },
      { name: 'Text to speech for ADHD', href: '/text-to-speech-for-adhd' },
      {
        name: 'Text to speech for students',
        href: '/text-to-speech-for-students',
      },
    ],
  },
];

export const languageCategories: Category[] = [
  {
    id: 'text-to-audio-languages',
    title: 'Text to speech languages',
    eyebrow: 'Native voices, 8 languages',
    previewBg: '#e6ede1',
    tools: [
      { name: 'English text to speech', href: '/english-text-to-speech' },
      { name: 'Spanish text to speech', href: '/spanish-text-to-speech' },
      { name: 'French text to speech', href: '/french-text-to-speech' },
      { name: 'Hindi text to speech', href: '/hindi-text-to-speech' },
      { name: 'Italian text to speech', href: '/italian-text-to-speech' },
      { name: 'Japanese text to speech', href: '/japanese-text-to-speech' },
      {
        name: 'Portuguese text to speech',
        href: '/portuguese-text-to-speech',
      },
      { name: 'Chinese text to speech', href: '/chinese-text-to-speech' },
    ],
  },
  {
    id: 'pdf-languages',
    title: 'PDF audio languages',
    eyebrow: 'Native voices for PDFs',
    previewBg: '#dde9eb',
    tools: [
      { name: 'Spanish PDF to audio', href: '/spanish-pdf-to-audio' },
      { name: 'French PDF to audio', href: '/french-pdf-to-audio' },
      { name: 'Hindi PDF to audio', href: '/hindi-pdf-to-audio' },
      { name: 'Italian PDF to audio', href: '/italian-pdf-to-audio' },
      { name: 'Japanese PDF to audio', href: '/japanese-pdf-to-audio' },
      { name: 'Portuguese PDF to audio', href: '/portuguese-pdf-to-audio' },
      { name: 'Chinese PDF to audio', href: '/chinese-pdf-to-audio' },
    ],
  },
];

export const useCaseCategories: Category[] = toolCategories;

export const categories: Category[] = [
  ...toolCategories,
  ...languageCategories,
];

export const Route = createFileRoute('/_marketing/tools')({
  beforeLoad: assertPublicToolsEnabled,
  head: () => ({
    meta: [
      ...seo({
        title:
          'AI Audio Tools for PDFs, Text, Articles, and Research | Listening',
        description:
          'Explore Listening tools for text to speech, PDF to audio, academic paper reading, audio note-taking, mobile read aloud, and natural AI voices.',
      }),
    ],
    links: [...hreflangLinks('/tools')],
  }),
  component: ToolsPage,
});

function ToolsPage() {
  return (
    <>
      <a
        className='sr-only focus:not-sr-only focus:absolute focus:z-50 focus:p-4'
        href='#main-content'
      >
        Skip to main content
      </a>

      <div className='bg-white text-[#0f0e0c]'>
        <main id='main-content' tabIndex={-1}>
          <GridBackground>
            <NavBar />
            <section
              aria-labelledby='tools-heading'
              className='mx-auto grid max-w-[1320px] grid-cols-1 items-end gap-10 px-6 pt-16 pb-14 sm:px-8 lg:grid-cols-[1.4fr_1fr] lg:gap-12 lg:pt-20'
            >
              <h1
                className='m-0 text-[clamp(44px,6.2vw,88px)] leading-[0.96] font-bold tracking-[-0.035em] text-balance'
                id='tools-heading'
              >
                Every audio tool you need,
                <br />
                <span className='font-bold text-[#5a564e]'>in one place.</span>
              </h1>
              <aside className='flex flex-col overflow-hidden rounded-3xl border border-[#e6dfd0] bg-white'>
                <div className='flex items-baseline justify-between gap-3 px-[22px] pt-5 pb-1.5'>
                  <span className='font-mono text-[12px] leading-none font-medium tracking-[0.06em] text-[#8e887d] uppercase'>
                    What you get
                  </span>
                  <span className='font-mono text-[12px] leading-none font-medium text-[#8e887d]'>
                    {String(
                      categories.reduce((sum, c) => sum + c.tools.length, 0),
                    ).padStart(2, '0')}{' '}
                    tools
                  </span>
                </div>
                <p className='mx-[22px] mt-2 mb-4 text-[17px] leading-[1.5] text-[#5a564e]'>
                  Turn text, PDFs, articles, and research papers into natural
                  audio. Six practical tool groups, plus language-specific pages
                  for text and PDFs.
                </p>
                <div className='mx-[22px] mb-4 flex flex-wrap gap-1.5'>
                  {[
                    'Read aloud',
                    'PDF',
                    'Voice',
                    'Web',
                    'Apps',
                    'Research',
                  ].map((tag) => (
                    <span
                      className='inline-flex h-[26px] items-center rounded-full border border-[#e6dfd0] bg-white px-2.5 font-mono text-[11px] font-medium text-[#5a564e]'
                      key={tag}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <div className='mt-auto flex items-center gap-3 border-t border-[#efe9da] px-[22px] py-3.5 text-[13px] font-medium'>
                  <span className='inline-flex items-center gap-2 text-[#0f0e0c]'>
                    <span
                      aria-hidden='true'
                      className='size-2 rounded-full bg-[#0587fb]'
                    />
                    Free · No sign-up required
                  </span>
                </div>
              </aside>
            </section>
          </GridBackground>

          <section
            aria-labelledby='tools-section-heading'
            className='mx-auto max-w-[1320px] px-6 pt-9 sm:px-8'
          >
            <div className='mb-7 max-w-3xl'>
              <p className='font-mono text-[12px] leading-none font-medium tracking-[0.06em] text-[#8e887d] uppercase'>
                Tools
              </p>
              <h2
                className='mt-3 text-[clamp(28px,3.4vw,44px)] leading-[1.05] font-bold tracking-[-0.03em] text-balance text-[#0f0e0c]'
                id='tools-section-heading'
              >
                Six ways to turn reading into audio
              </h2>
              <p className='mt-4 max-w-2xl text-[16px] leading-[1.55] text-[#5a564e]'>
                Pick the workflow that matches what you have in front of you:
                pasted text, PDFs, articles, mobile reading, browser tabs, or
                research papers.
              </p>
            </div>
            <div className='grid grid-cols-1 gap-7 pb-16 md:grid-cols-2 lg:grid-cols-3'>
              {toolCategories.map((category) => (
                <CategoryCard category={category} key={category.id} />
              ))}
            </div>
          </section>

          <section aria-labelledby='languages-section-heading'>
            <div className='mx-auto max-w-[1320px] px-6 pt-16 pb-20 sm:px-8 lg:pt-20 lg:pb-24'>
              <div className='mb-7 max-w-3xl'>
                <p className='font-mono text-[12px] leading-none font-medium tracking-[0.06em] text-[#8e887d] uppercase'>
                  Languages
                </p>
                <h2
                  className='mt-3 text-[clamp(28px,3.4vw,44px)] leading-[1.05] font-bold tracking-[-0.03em] text-balance text-[#0f0e0c]'
                  id='languages-section-heading'
                >
                  Natural voices for text and PDF pages
                </h2>
                <p className='mt-4 max-w-2xl text-[16px] leading-[1.55] text-[#5a564e]'>
                  Use native voices for text-to-speech pages, or upload PDFs in
                  Spanish, French, Hindi, Italian, Japanese, Portuguese, and
                  Chinese.
                </p>
              </div>
              <div className='grid grid-cols-1 gap-7 md:grid-cols-2 lg:grid-cols-3'>
                {languageCategories.map((category) => (
                  <CategoryCard category={category} key={category.id} />
                ))}
              </div>
            </div>
          </section>
        </main>
      </div>

      <div role='complementary'>
        <FAQButton />
        <TryListeningBanner />
        <Footer />
      </div>
    </>
  );
}

export function CategoryCard({ category }: { category: Category }) {
  return (
    <article className='flex flex-col overflow-hidden rounded-3xl border border-[#e6dfd0] bg-white transition-all duration-300 hover:-translate-y-0.5 hover:border-[#0f0e0c]/15 hover:shadow-[0_1px_0_rgba(20,16,8,0.04),0_12px_32px_-20px_rgba(20,16,8,0.18)]'>
      <div
        className='relative aspect-[16/9] overflow-hidden border-b border-[#efe9da]'
        style={{ backgroundColor: category.previewBg }}
      >
        <CategoryIllustration id={category.id} />
      </div>

      <div className='px-[22px] pt-5 pb-1.5'>
        <span className='font-mono text-[12px] leading-none font-medium tracking-[0.06em] text-[#8e887d] uppercase'>
          {category.eyebrow}
        </span>
      </div>

      <h2 className='mx-[22px] mt-1 mb-3.5 text-[24px] leading-[1.1] font-bold tracking-[-0.02em] text-[#0f0e0c]'>
        {category.title}
      </h2>

      <div className='flex flex-col px-[22px] pb-[22px]'>
        {category.tools.map((tool, index) => (
          <a
            className={`group/link flex items-center justify-between py-2.5 text-[15px] font-medium text-[#0f0e0c] transition-[padding] duration-150 hover:pl-1.5 ${
              index === 0 ? '' : 'border-t border-[#efe9da]'
            }`}
            href={localizeHref(tool.href)}
            key={tool.href}
          >
            <span className='underline decoration-1 underline-offset-[3px] group-hover/link:decoration-2'>
              {tool.name}
            </span>
            <ArrowGlyph />
          </a>
        ))}
      </div>
    </article>
  );
}

function ArrowGlyph() {
  return (
    <span
      aria-hidden='true'
      className='inline-flex size-[22px] -translate-x-1 items-center justify-center rounded-full text-[#8e887d] opacity-0 transition-all duration-150 group-hover/link:translate-x-0 group-hover/link:text-[#0f0e0c] group-hover/link:opacity-100'
    >
      <svg
        fill='none'
        height='12'
        stroke='currentColor'
        strokeWidth='1.8'
        viewBox='0 0 12 12'
        width='12'
      >
        <path
          d='M2 6h8M7 3l3 3-3 3'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </svg>
    </span>
  );
}

const categoryPreviewImages: Partial<Record<CategoryId, string>> = {
  'read-aloud-tools': textToAudioPng,
  'pdf-documents': pdfToolsPng,
  'voice-generators': textToAudioPng,
  'articles-web': webToAudioPng,
  'apps-extensions-desktop': mobileToolPng,
  'research-study': researchToolPng,
  'text-to-audio-languages': textToAudioLanguagesPng,
  'pdf-languages': pdfLanguagesPng,
};

function CategoryIllustration({ id }: { id: CategoryId }) {
  const src = categoryPreviewImages[id] ?? textToAudioPng;

  return (
    <img
      alt=''
      aria-hidden='true'
      className='block size-full object-contain'
      loading='lazy'
      src={src}
    />
  );
}
